// PLT-2165: the second way a chat moves to WhatsApp. The visitor types a
// number, apps/ai sends the org's handoff template to it, and a tap on the
// template's Continue button claims the code. apps/ai enforces the limits
// (three sends per conversation, one minute apart); these helpers only mirror
// them so the card can show the timer and drop the form after the last send.

/** Mirrors `HANDOFF_MAX_SENDS` in `@tkana/db`. */
export const HANDOFF_MAX_SENDS = 3;

/** Where the card stands for one thread. */
export type HandoffSendState = {
	threadId: string;
	/** The masked number the last send went to, as apps/ai returned it. */
	sentTo: string | null;
	sendCount: number;
	/** Epoch ms before which Send stays disabled. */
	nextSendAt: number;
};

export type HandoffSendResult =
	| { ok: true; sentTo: string; sendCount: number; retryAfterSeconds: number }
	| { ok: false; reason: "too_soon"; retryAfterSeconds: number }
	| { ok: false; reason: "limit" }
	| { ok: false; reason: "invalid_phone" }
	| { ok: false; reason: "failed" };

/** The state to keep after a send attempt for `threadId`. */
export function handoffSendStateAfter(
	current: HandoffSendState | null,
	threadId: string,
	result: HandoffSendResult,
	now: number = Date.now(),
): HandoffSendState {
	const base: HandoffSendState =
		current && current.threadId === threadId
			? current
			: { threadId, sentTo: null, sendCount: 0, nextSendAt: 0 };
	if (result.ok) {
		return {
			threadId,
			sentTo: result.sentTo,
			sendCount: result.sendCount,
			nextSendAt: now + result.retryAfterSeconds * 1000,
		};
	}
	if (result.reason === "too_soon") {
		return { ...base, nextSendAt: now + result.retryAfterSeconds * 1000 };
	}
	if (result.reason === "limit") {
		return { ...base, sendCount: HANDOFF_MAX_SENDS };
	}
	return base;
}

/** Whether the phone form still shows: a later thread starts over. */
export function canStillSend(
	state: HandoffSendState | null,
	threadId: string | null | undefined,
): boolean {
	if (!threadId) return false;
	if (!state || state.threadId !== threadId) return true;
	return state.sendCount < HANDOFF_MAX_SENDS;
}

/** Whole seconds until Send is allowed again, never negative. */
export function secondsUntilSend(
	state: HandoffSendState | null,
	threadId: string | null | undefined,
	now: number = Date.now(),
): number {
	if (!state || !threadId || state.threadId !== threadId) return 0;
	return Math.max(0, Math.ceil((state.nextSendAt - now) / 1000));
}

/** "0:42" for the Resend button. */
/**
 * How many digits the visitor has typed, Arabic-Indic (٠-٩) and Persian
 * (۰-۹) digits included. A plain `\D` strip drops them, so an Arabic-typed
 * number never enabled Send. apps/ai normalizes the same digits.
 */
export function phoneDigitCount(phone: string): number {
	return (phone.match(/[0-9\u0660-\u0669\u06F0-\u06F9]/g) ?? []).length;
}

export function formatCountdown(seconds: number): string {
	const s = Math.max(0, Math.floor(seconds));
	return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/**
 * Ask the worker to send the handoff template to `phone`. Never throws: any
 * failure the card cannot act on resolves to `failed`.
 */
export async function requestHandoffSend(input: {
	chatUrl: string;
	widgetId: string;
	threadId: string;
	phone: string;
	language: "ar" | "en";
}): Promise<HandoffSendResult> {
	try {
		const response = await fetch(`${input.chatUrl}/handoff/send`, {
			method: "POST",
			headers: { "Content-Type": "application/json", "x-widget-id": input.widgetId },
			body: JSON.stringify({
				threadId: input.threadId,
				phone: input.phone,
				language: input.language,
			}),
		});
		const body = (await response.json().catch(() => ({}))) as {
			sentTo?: unknown;
			sendCount?: unknown;
			retryAfterSeconds?: unknown;
			reason?: unknown;
		};
		const retry =
			typeof body.retryAfterSeconds === "number" && body.retryAfterSeconds > 0
				? body.retryAfterSeconds
				: 60;
		if (response.ok && typeof body.sentTo === "string" && typeof body.sendCount === "number") {
			return { ok: true, sentTo: body.sentTo, sendCount: body.sendCount, retryAfterSeconds: retry };
		}
		if (body.reason === "too_soon") return { ok: false, reason: "too_soon", retryAfterSeconds: retry };
		if (body.reason === "limit") return { ok: false, reason: "limit" };
		if (body.reason === "invalid_phone") return { ok: false, reason: "invalid_phone" };
		return { ok: false, reason: "failed" };
	} catch {
		return { ok: false, reason: "failed" };
	}
}
